import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';

import { STATUS_VALUES, type StatusValue } from '../../core/filters/filter-state.model';
import { FaIconComponent, faChevronDown } from '../../ui/icons/app-icons.registry';

/**
 * The status filter: a disclosure button over a native checkbox group across
 * the fixed three values (FR-007, R-006).
 *
 * Checkboxes rather than a combobox: the value set is closed and multi-select,
 * so there is nothing to search and no combobox machinery to justify (R-005) —
 * and a `<fieldset>`/`<legend>` group is natively accessible. Zero checked is
 * the default and means *no status constraint*, which collapses to the same
 * absent-parameter URL as Reset (FR-014).
 *
 * The selection is canonicalised on every write (R-006): the same choice always
 * yields the same `status` CSV, whatever order the user checked the boxes in,
 * which is what makes a shared link byte-stable (SC-002).
 */
@Component({
  selector: 'app-status-multi-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FaIconComponent],
  templateUrl: './status-multi-select.component.html',
  styleUrl: './status-multi-select.component.scss',
  // Escape is handled on the host rather than on the panel: it must also close
  // the panel while focus is still on the disclosure button, and a bare <div>
  // with a key handler would not be focusable (a11y lint, WCAG 2.1.1).
  host: { '(keydown.escape)': 'onEscape($event)' },
})
export class StatusMultiSelectComponent {
  readonly value = input<readonly StatusValue[]>([]);

  /**
   * Bumped by the bar on Reset and on the FR-019 session clear. This control
   * displays no draft — the only local state is whether the panel is open — but
   * an open panel is still local state that no `value` write can close (Reset
   * over an already-empty selection writes an equal array). Leaving one control
   * holding a previous owner's open popup is the same defect as a stale draft.
   */
  readonly resetToken = input(0);

  readonly valueChange = output<readonly StatusValue[]>();

  private readonly toggleElement = viewChild.required<ElementRef<HTMLButtonElement>>('statusToggle');
  private readonly expanded = signal(false);

  protected readonly chevronIcon = faChevronDown;
  protected readonly statusValues = STATUS_VALUES;
  protected readonly open = this.expanded.asReadonly();
  protected readonly panelId = 'filter-status-panel';

  protected readonly summary = computed(() => {
    const selected = this.value();
    if (selected.length === 0) return 'All';
    if (selected.length === 1) return selected[0];
    return `${selected.length} selected`;
  });

  constructor() {
    // An explicit Reset (or session clear) closes the panel. Focus is left
    // where it is: Escape returns focus to the toggle because the user asked
    // this control to close, but a Reset is a click elsewhere in the bar.
    // The initial run is skipped, matching the bar's other controls.
    let lastResetToken = untracked(() => this.resetToken());
    effect(() => {
      const token = this.resetToken();
      if (token === lastResetToken) return;
      lastResetToken = token;
      untracked(() => this.expanded.set(false));
    });
  }

  protected checkboxId(status: StatusValue): string {
    return `filter-status-${status}-input`;
  }

  protected isChecked(status: StatusValue): boolean {
    return this.value().includes(status);
  }

  protected toggleOpen(): void {
    this.expanded.update((open) => !open);
  }

  protected onCheckboxChange(status: StatusValue, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const selected = new Set(this.value());
    if (checked) {
      selected.add(status);
    } else {
      selected.delete(status);
    }

    // Canonical order enforced on write, never on read (R-006).
    this.valueChange.emit(STATUS_VALUES.filter((candidate) => selected.has(candidate)));
  }

  protected onEscape(event: KeyboardEvent): void {
    if (!this.expanded()) return;
    event.preventDefault();
    this.expanded.set(false);
    this.toggleElement().nativeElement.focus();
  }
}
